<template>
  <page-view
    :menu="pageMenu"
    :customClass="customClass"
    @tab="handleRouteChange"
  >
    <template #header>
      <base-header title="Analytics" :menu="headerMenu">
        <template #filter>
          <search-videos-menu :video_external_id="video_external_id" />
        </template>
      </base-header>
    </template>

    <template #filter>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <div
            v-bind="attrs"
            v-on="isCurrentCycle && isBandwidthRoute ? on : ''"
          >
            <date-picker
              :shortcuts="shortcuts"
              :clearable="false"
              :disabled-date="verifyDisabledDate"
              popup-class="analyticsDatePicker"
              :class="{
                'opacity-analytics': isCurrentCycle && isBandwidthRoute,
              }"
              style="width: 260px"
              v-model="dateRange"
              editable
              :disabled="isCurrentCycle && isBandwidthRoute"
              confirm
              range
            >
              <template v-slot:input>
                <v-text-field
                  :class="{ analyticsPickerField: $vuetify.theme.dark }"
                  dense
                  :value="dateRangeValue"
                  outlined
                  prepend-inner-icon="mdi-calendar-month"
                  readonly
                  hide-details
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="primary" class="mr-2">
                      mdi-calendar-month
                    </v-icon>
                  </template>
                </v-text-field>
              </template>
              <template v-slot:icon-calendar>
                <v-icon class="primary" style="display: none" color="primary">
                  mdi-calendar-month
                </v-icon>
              </template>
            </date-picker>
          </div>
        </template>
        <span>{{
          $t('message.pages.video.metricsPage.tooltipCurrentCycle')
        }}</span>
      </v-tooltip>
    </template>

    <template #content>
      <component
        :is="componentSegment[selectedItem].component"
        :component-data="componentData"
        :has-any-video-in-filter="hasAnyVideoInFilter"
        @fetched="handlePosFetch"
      />
    </template>
  </page-view>
</template>

<script>
// external imports
import { subDays, subMonths, isBefore, format } from 'date-fns'
import { mapState, mapMutations } from 'vuex'
import DatePicker from 'vue2-datepicker'
import _ from 'lodash'
// internal imports

// API
import videosApi from '@/services/videosApi'

// Components
import Views from './Views.vue'
import Retention from './Retention.vue'
import Devices from './Devices.vue'
import Browsers from './Browsers.vue'
import Locales from './Locales.vue'
import Bandwidth from './Bandwidth.vue'
import Plays from './Plays.vue'
import References from './References.vue'
import PageView from '@/layout/PageView.vue'
import Storage from '@/utils/Storage.js'

// Others
import 'vue2-datepicker/locale/pt-br'

export default {
  name: 'AnalyticsV2',
  components: {
    DatePicker,
    Views,
    Retention,
    Locales,
    Bandwidth,
    Devices,
    Browsers,
    References,
    PageView,
    Plays,
    Storage,
    BaseHeader: () => import('@/components/BaseHeader.vue'),
    SearchVideosMenu: () => import('./SearchVideosMenu.vue'),
  },
  data: () => ({
    isDotCom: false,
    selectedVideos: [],
    selectedItem: 0,
    windowWidth: window.innerWidth,
    hasAnyVideoInFilter: false,
    isCurrentCycle: true,
    dateRange: [],
    toBeFilteredVideos: [],
    video_external_id: null,
    isBeta: false,
  }),
  computed: {
    ...mapState({
      isAdBlock: (state) => state.dashboard.isAdBlockEnabled,
      planInfo: (state) => state.user.userPlanInfo,
    }),
    customClass() {
      return this.isBeta || this.isDotCom ? 'beta' : ''
    },
    dateRangeValue() {
      const mask = (date) => format(date, 'dd/MM/yyyy')
      const [start, end] = _.isArray(this.dateRange)
        ? this.dateRange
        : [this.dateRange, this.dateRange]
      Storage.setItem('datePickerRange', [start, end])

      return `${mask(start)} ~ ${mask(end)}`
    },
    isAdBlockEnabled() {
      if (this.isAdBlock) {
        return this.isAdBlock
      }
      return false
    },
    tableHeight() {
      if (this.videosData.length < 5) {
        return 288
      } else if (this.videosData.length <= 100) {
        return (this.videosData.length + 1) * 48 + 5
      } else {
        return 4805
      }
    },
    shortcuts() {
      const i18n = this.$i18next

      const today = {
        text: i18n.t('message.pages.video.metricsPage.datepicker.today'),
        onClick: () => new Date(),
      }

      const days = [
        {
          text: i18n.t('message.pages.video.metricsPage.datepicker.yesterday'),
          onClick() {
            const date = new Date()
            return [subDays(date, 1), date]
          },
        },
        {
          text: i18n.t('message.pages.video.metricsPage.datepicker.7days'),
          onClick() {
            const date = new Date()
            return [subDays(date, 7), date]
          },
        },
        {
          text: i18n.t('message.pages.video.metricsPage.datepicker.aMonth'),
          onClick() {
            const date = new Date()

            return [subMonths(date, 1), date]
          },
        },
      ]

      return this.toBeFilteredVideos.length ? [today, ...days] : days
    },
    headerMenu() {
      const i18n = this.$i18next
      return [
        {
          title: i18n.t('message.pages.analytics.history'),
          to: { name: 'AnalyticsV2Account' },
        },
        {
          title: i18n.t('message.pages.analytics.realtimeTitle'),
          to: { name: 'AnalyticsV2Realtime' },
        },
      ]
    },
    componentSegment() {
      const isBeta = this.isBeta
      const isDotCom = this.isDotCom
      const i18n = this.$i18next

      return [
        {
          title: i18n.t('message.routesTitles.AnalyticsV2Account'),
          to: { name: 'AnalyticsV2Account' },
          component: 'Views',
          toShow: isBeta || true,
        },
        {
          title: i18n.t('message.routesTitles.AnalyticsV2Retention'),
          to: { name: 'AnalyticsV2Retention' },
          component: 'Retention',
          toShow: isBeta || true,
        },
        {
          title: i18n.t('message.routesTitles.AnalyticsV2Devices'),
          to: { name: 'AnalyticsV2Devices' },
          component: 'Devices',
          toShow: isBeta || true,
        },
        {
          title: i18n.t('message.routesTitles.AnalyticsV2Browsers'),
          to: { name: 'AnalyticsV2Browsers' },
          component: 'Browsers',
          toShow: isBeta || true,
        },
        {
          title: i18n.t('message.routesTitles.AnalyticsV2Bandwidth'),
          to: { name: 'AnalyticsV2Bandwidth' },
          component: 'Bandwidth',
          toShow: isBeta || true,
        },
        {
          title: i18n.t('message.routesTitles.AnalyticsV2Play'),
          to: { name: 'AnalyticsV2Play' },
          component: 'Plays',
          toShow: isBeta || isDotCom,
        },
        {
          title: i18n.t('message.routesTitles.AnalyticsV2Locale'),
          to: { name: 'AnalyticsV2Locale' },
          component: 'Locales',
          toShow: isBeta || true,
        },
        {
          title: i18n.t('message.routesTitles.AnalyticsV2References'),
          to: { name: 'AnalyticsV2References' },
          component: 'References',
          toShow: isBeta || true,
        },
      ]
    },
    isBandwidthRoute() {
      return this.$route.path === '/analytics/bandwidth'
    },
    componentData() {
      return {
        selectedVideos: this.toBeFilteredVideos,
        dateRange: _.isArray(this.dateRange)
          ? this.dateRange
          : [this.dateRange, this.dateRange],
      }
    },
    pageMenu() {
      return this.componentSegment.filter(({ toShow }) => toShow)
    },
  },
  async mounted() {
    Storage.removeItem('datePickerRange')

    this.isBeta = location.search.match('beta')
    this.isDotCom = !location.host.includes('.com.br')
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize)
    })
    this.handleRouteChange()
    if (!this.planInfo?.limit) {
      this.$root.$on('onFinishLoadPlanInfo', () => {
        this.resetDateRange()
      })
    }
    if (!window.db) {
      this.$root.$emit('initDexie')
    }
    this.resetDateRange()
    await this.checkIfVideosAreLoaded()

    this.$root.$on('filterSelected', (selectedVideos, hasAnyVideoInFilter) => {
      this.toBeFilteredVideos = [...selectedVideos]
      this.hasAnyVideoInFilter = hasAnyVideoInFilter
    })
    this.$root.$on('isCurrentCycle', (isCurrentCycle) => {
      this.isCurrentCycle = isCurrentCycle
      if (isCurrentCycle) this.resetDateRange()
    })
    this.$root.$on('analyticsV2SearchAll', () => {
      this.toBeFilteredVideos = []
      this.hasAnyVideoInFilter = false
      this.searchVideoText = ''
    })

    this.$root.$emit('changeNavItem', 6) // emit event to set nav index
  },
  watch: {
    shortcuts: {
      handler() {
        this.resetDateRange()
      },
      deep: true,
    },
  },
  beforeDestroy() {
    this.$root.$off('filterSelected')
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    ...mapMutations('dashboard', ['UPDATE_IS_ADBLOCK_ENABLED']),
    resetDateRange() {
      let startFormatted, endFormatted
      if (this.planInfo?.limit?.start_period) {
        startFormatted = new Date(this.planInfo?.limit?.start_period)
        endFormatted = new Date(this.planInfo?.limit?.end_period)
      } else {
        const newDate = new Date()
        const yesterday = newDate.setDate(newDate.getDate() - 1)
        this.dateRange = [subDays(new Date(), 7), new Date(yesterday)]
      }
      this.dateRange = [startFormatted, endFormatted]
    },
    async checkIfVideosAreLoaded() {
      const allVideos = await window.db.videos.toArray()
      if (!allVideos.length) {
        const { data } = await videosApi.getAllVideosAndBin()
        const videosResponse = data.videos
        let filteredFoldersIds = []
        for (let folderId in videosResponse) {
          filteredFoldersIds.push(folderId)
        }
        await window.db.videos.clear()
        for (let folderId of filteredFoldersIds) {
          let arrToBeAdded = videosResponse[folderId].map((obj) => ({
            ...obj,
            folder_id: folderId,
          }))
          await window.db.videos.bulkPut(arrToBeAdded)
        }
      }
    },
    onResize() {
      this.windowWidth = window.innerWidth
    },
    verifyDisabledDate(date) {
      const today1 = new Date()
      const today2 = new Date()

      const yesterday = today1.setDate(today1.getDate() - 1)
      today2.setHours(0, 0, 0, 0)

      return this.toBeFilteredVideos.length
        ? date > today2
        : !isBefore(date, yesterday)
    },
    handleRouteChange() {
      const found = this.componentSegment.findIndex(
        ({ to }) => to.name === this.$route.name
      )
      this.selectedItem = found === -1 ? 0 : found
    },
    handlePosFetch({ source }) {
      switch (source) {
        case 'view':
          if (this.$route.query.v) this.video_external_id = this.$route.query.v
          break

        default:
          break
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.opacity-analytics {
  opacity: 0.5 !important;
  pointer-events: none !important;
}
*::v-deep {
  @import '@/assets/scss/src/analytics';

  .header__tabs.v-tabs {
    position: relative;
    left: -85px;
  }

  .page-view__content {
    margin: {
      left: $spacing-minus-2 !important;
      right: $spacing-minus-2 !important;
    }

    max-width: 1000px;

    @media (min-width: 680px) and (max-width: 1540px) {
      margin: {
        left: $spacing-plus-1 !important;
        right: $spacing-plus-1 !important;
      }
    }

    @media (min-width: 1540px) {
      margin: {
        left: auto !important;
        right: auto !important;
      }
    }
  }

  .page-view__content.beta {
    @media (min-width: 680px) and (max-width: 1540px) {
      margin: {
        left: 4.5rem !important;
        right: $spacing-plus-1 !important;
      }
    }
  }
}
</style>

<style lang="scss">
$learnMoreBtnSize: 14px;
@import '~vue2-datepicker/scss/index.scss';
.svgSize {
  width: 450px;
}

.main-scroll {
  border: none !important;
}

.navText {
  font-weight: 400 !important;
  font-size: 16px !important;
  line-height: 150% !important;
}

.analyticsTable .header-cell {
  justify-content: flex-start !important;
  padding-left: 20px !important;
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
}

.analyticsTable .item-cell {
  border-bottom: 0px !important;
  border-top: 1px solid var(--v-contrast-base);
}

.analyticsTable {
  overflow-x: hidden !important;
  background-color: var(--v-dark-newBackground);
}

.analyticsTable .t-header {
  display: none;
}

.bg-dark .analyticsTable .item-line {
  background-color: var(--v-neutral-darken2) !important;
}

.darkThemeSvgFix {
  svg text {
    fill: white;
  }
  .apexcharts-legend-text {
    color: white !important;
  }
}

.analyticsPickerField {
  background-color: #373b54;
}

.mx-datepicker-sidebar {
  overflow: hidden !important;
}

.analyticsHeader {
  border-bottom: 1px solid #e6e8f8;
}

.analyticsNavigation .v-list-item--active {
  border: 1px solid #a0acff;
  border-radius: 4px;
}

.analyticsDatePicker {
  .mx-datepicker-btn-confirm {
    background-color: var(--v-brand-base);
    color: var(--v-neutral-lighten4);

    &:hover {
      background-color: var(--v-brand-darken1);
      border-color: var(--v-brand-darken1);
    }

    &:active {
      background-color: var(--v-brand-darken2);
      border-color: var(--v-brand-darken2);
    }
  }
}

.bg-dark {
  .header__title--main {
    color: var(--v-brand-lighten2);
  }

  .data-table {
    &__item {
      &:hover {
        background-color: var(--v-neutral-darken2);
      }
    }
  }
}

.analytics__learnMore--button i {
  font-size: 0.7rem !important;
  width: $learnMoreBtnSize !important;
  height: $learnMoreBtnSize !important;
}
</style>
